import { tracker } from '@lessonup/analytics';
import { logger, useDocumentMutation, useLocale } from '@lessonup/client-integration';
import { newPin, newQuizPin, stringToProsemirror } from '@lessonup/pins-shared';
import {
  Button,
  ErrorBanner,
  IconAdd,
  SelectField,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
  TextField,
  useErrorContext,
  useTranslation,
} from '@lessonup/ui-components';
import { AppError, asError, wait } from '@lessonup/utils';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FullPageBodySection } from '../../../components/FullPageFlow/FullPageBodySection';
import { AiToolboxGenerateQuizInput } from '../../../types/graphql/types.generated';
import { lessonEditorV2Route } from '../../../utils/bridge/teacherRoutes';
import { getLanguageValue } from '../../../utils/language/languageOptions';
import { ReviewStep } from '../../ChatGPTGenerator/components/Wizard/ReviewStep/ReviewStep';
import { createTranslatedTemplate } from '../../Editor/utils/createTranslatedTemplate';
import { getQuestionComponentInInteractivePin } from '../../Editor/utils/pin/pin.utils';
import { AiToolboxGenerateQuizDocument, AiToolboxGenerateQuizMutation } from '../AIToolBox.graphql.generated';
import { QuizGenerator } from '../AiToolbox.types';
import { i18nextNamespaceAiTools } from '../AiToolbox.utils';
import { useAiToolboxContext } from '../context/AiToolboxContext';
import { ComprehensionLevelSelector } from '../helpers/ComprehensionLevelSelector';
import { DefaultToolWrapper, FullWidth } from '../helpers/DefaultToolWrapper';
import { LanguageSelector, transformLanguageFormData } from '../helpers/LanguageSelector';
import { LessonupUrlOrSubject } from '../helpers/LessonupUrlOrSubject';
import { CreateLessonForAiToolsDocument } from '../helpers/SaveOutput/SaveGeneratedOutput.graphql.generated';
import { ThumbUpAndDownFeedback } from '../helpers/ThumbUpAndDownFeedback';
import { AiToolboxFormWrapper } from '../helpers/ToolForm';
import { tipTapJSONToHtml } from '../helpers/tiptap';
import { useInitialBodyFormUpdate } from '../hooks/useInitialBodyFormUpdate';

interface AiToolboxQuizProps {
  generator: QuizGenerator;
}

export function AiToolboxQuiz({ generator }: AiToolboxQuizProps) {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const currentLanguage = useLocale().language;
  const { initialBody, showInline } = useAiToolboxContext();

  const methods = useForm<AiToolboxGenerateQuizInput>({
    defaultValues: {
      subject: tipTapJSONToHtml(initialBody),
      numberOfQuestions: 5,
      language: getLanguageValue(currentLanguage),
    },
  });
  const { handleSubmit, register, setValue } = methods;
  const { setError } = useErrorContext();

  useInitialBodyFormUpdate(setValue, 'subject');

  const [data, setData] = useState<IdentifiableQuizItem[] | null>(null);
  const [title, setTitle] = useState<string>('');
  const [quizGeneratorLoading, setQuizGeneratorLoading] = useState(false);
  const [formData, setFormData] = useState<AiToolboxGenerateQuizInput | null>(null);

  const [generateQuizMutation] = useDocumentMutation(AiToolboxGenerateQuizDocument, {
    onError: (error) => {
      setError({ error, displayMode: 'banner' });
    },
    onCompleted: (data) => {
      setData(mapQuizResponseToGeneratedResult(data.aiToolboxGenerateQuiz.questions));
      setTitle(data.aiToolboxGenerateQuiz.title ?? '');
    },
  });

  const onSubmit = async (input: AiToolboxGenerateQuizInput) => {
    try {
      setQuizGeneratorLoading(true);
      const correctedFormData = transformLanguageFormData(input);
      const formData = {
        ...correctedFormData,
        numberOfQuestions:
          typeof input.numberOfQuestions === 'string' ? parseInt(input.numberOfQuestions) : input.numberOfQuestions,
        comprehensionLevel: input.comprehensionLevel,
      };

      setFormData(formData);

      tracker.events.aiToolsQuizPrepare({
        educationalLevel: formData.comprehensionLevel || '',
        languageOutput: formData.language,
        lessonAsInput: !!formData.lessonupUrl,
        numberOfResults: formData.numberOfQuestions,
        promptLength: formData.subject?.length ?? 0,
      });

      await generateQuizMutation({
        variables: {
          input: {
            ...formData,
          },
        },
      });
    } catch (error) {
      setError({ error: asError(error), displayMode: 'banner' });
      logger.error(asError(error));
    } finally {
      setQuizGeneratorLoading(false);
    }
  };

  return (
    <DefaultToolWrapper>
      <AiToolboxFormWrapper generator={generator}>
        <FormProvider {...methods}>
          <SpaceBetween direction="y" spacing={showInline ? spacing.size16 : spacing.size32}>
            <LessonupUrlOrSubject lessonUrlName="lessonupUrl" subjectName="subject" />

            <SelectField
              label={t('quizInputNumberOfQuestions')}
              size="medium"
              options={Array.from({ length: 15 }, (_, i) => ({ label: (i + 1).toString(), value: i + 1 }))}
              {...register('numberOfQuestions', { required: true })}
            />

            <ComprehensionLevelSelector<AiToolboxGenerateQuizInput> register={register} />
            <LanguageSelector<AiToolboxGenerateQuizInput> />

            <ErrorBanner />

            <Button loading={quizGeneratorLoading} onClick={handleSubmit(onSubmit)}>
              {t('generateQuiz')}
            </Button>
          </SpaceBetween>
        </FormProvider>
      </AiToolboxFormWrapper>

      {data && <QuizResult title={title} data={data} formData={formData} />}
    </DefaultToolWrapper>
  );
}

function QuizResult({
  title,
  data,
  formData,
}: {
  title: string;
  data: IdentifiableQuizItem[];
  formData: AiToolboxGenerateQuizInput | null;
}) {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const { addPins, showInline } = useAiToolboxContext();
  const [selection, setSelection] = useState<string[]>([]);
  const [lessonCreationLoading, setLessonCreationLoading] = useState(false);
  const [createLesson] = useDocumentMutation(CreateLessonForAiToolsDocument, {});
  const { setError, clearError } = useErrorContext();

  const disableSaveButton = !selection.length;

  const { register, handleSubmit, setValue } = useForm<NewLessonFormData>({
    defaultValues: {
      name: title ?? t(`defaultLessonName`),
    },
  });

  useEffect(() => {
    if (!title) return;
    // if they regenerate, we need to update the name
    setValue('name', title);
  }, [title, setValue]);

  const createPinsFromFormSelection = () => {
    const selectedItems = data?.filter((x) => selection.includes(x.tempId));

    const template = createTranslatedTemplate();

    const pins = selectedItems?.map((x) => {
      const quizPin = newQuizPin(
        template.layouts.quizPinLayouts[0],
        x.correctAnswers.map((x) => `answer-${x - 1}`),
        x.answers.map((y, i) => ({ id: `answer-${i}`, type: 'TEXT', text: y }))
      );

      const questionComponent = getQuestionComponentInInteractivePin(quizPin);
      questionComponent.settings.tipTapContent = stringToProsemirror(x.question, {
        bold: true,
      });

      return newPin(quizPin);
    });

    return pins;
  };

  const onAddAsSlides = async () => {
    const pins = createPinsFromFormSelection();

    if (addPins) {
      await addPins(pins);
    }
  };

  const onSaveToLesson = async ({ name }: NewLessonFormData) => {
    try {
      setLessonCreationLoading(true);
      clearError();

      const pins = createPinsFromFormSelection();

      const response = await createLesson({
        variables: {
          input: {
            pins,
            name,
          },
        },
      });

      if (formData) {
        tracker.events.aiToolsQuizCreate({
          educationalLevel: formData.comprehensionLevel || '',
          lessonAsInput: !!formData.lessonupUrl,
          numberOfResults: pins.length,
          languageOutput: formData.language,
          promptLength: formData.subject?.length ?? 0,
        });
      }

      if (response.data?.createLesson.lesson.id) {
        window.location.href = lessonEditorV2Route.href({ lessonId: response.data?.createLesson.lesson.id });
        // Wait for the lesson to be loaded, 5000 ms is enough time for the lesson to be loaded,
        // and short enough to not lock the user if nothing happens
        await wait(5000);
      } else {
        throw new AppError('unexpected-data', 'Failed to create lesson');
      }
    } catch (error) {
      logger.error(asError(error));
      setError({ error: asError(error), displayMode: 'toast' });
    } finally {
      setLessonCreationLoading(false);
    }
  };

  const quizResults = (
    <FullWidth>
      <ReviewStep
        generatedResults={data.map((x) => {
          return {
            tempId: x.tempId,
            content: {
              type: 'quiz',
              question: x.question,
              answers: x.answers,
              correctAnswers: x.correctAnswers,
            },
          };
        })}
        setSelection={(selection) => setSelection(selection)}
        selection={selection}
        isCompleted={true}
        noPadding
      />
      <SpaceBetween direction="y" spacing={spacing.size16}>
        {!showInline && (
          <>
            <TextField label={t('quizInputCreateLessonName')} {...register('name')} size="medium" />
            <TextElement size="small" textStyle="caption">
              {t('quizCreateLessonInstruction')}
            </TextElement>
          </>
        )}
        <StyledButtonWrapper>
          {!showInline && (
            <Button loading={lessonCreationLoading} disabled={disableSaveButton} onClick={handleSubmit(onSaveToLesson)}>
              {t('quizCreateLessonButton')}
            </Button>
          )}
          {showInline && (
            <Button
              buttonType="neutral"
              showStroke
              onClick={handleSubmit(onAddAsSlides)}
              iconStart={<IconAdd />}
              disabled={!selection.length}
            >
              {selection.length > 1 ? t('addAsSlidesButton') : t('addAsSlideButton')}
            </Button>
          )}
          <ThumbUpAndDownFeedback aiToolType="quiz" />
        </StyledButtonWrapper>
      </SpaceBetween>
    </FullWidth>
  );

  return showInline ? quizResults : <FullPageBodySection>{quizResults}</FullPageBodySection>;
}

function mapQuizResponseToGeneratedResult(response: QuizItem[]): IdentifiableQuizItem[] {
  return response.map((x, i) => ({
    ...x,
    tempId: `question-${i}`,
  }));
}

type QuizItem = Omit<AiToolboxGenerateQuizMutation['aiToolboxGenerateQuiz']['questions'][number], '__typename'>;

type IdentifiableQuizItem = QuizItem & {
  tempId: string;
};

interface NewLessonFormData {
  name: string;
}

const StyledButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${spacing.size16};
  gap: ${spacing.size8};

  @media print {
    display: none;
  }
`;
